import React from "react";
import { Box, Button, Text, Icon } from "@showitinc/showit-wc";

import useRendererLock from "@/hooks/query/useRendererLock.mjs";
import { useSiteState } from "@/hooks/query/useSite";
import ProtorendererToggle from "@/app2components/modals/ProtorendererToggle";
import AppConstants from "@/constants/AppConstants";
import SiteSettingsActions from "@/actions/SiteSettingsActions";
import SitePublishActions from "@/actions/SitePublishActions";
import UIActions from "@/actions/UIActions";
import UserActions from "@/actions/UserActions";
import { PREVIEW, PUBLISH_OPEN } from "@/constants/UserEventConstants";
import styles from "./UpgradePanel.module.css";
import useRenderer from "@/hooks/query/useRenderer";

/**
 * Returns eligibility for each step
 * @param {import("@/service/SiteService.mjs").RendererLockEligibility} param0
 * @param {boolean} isV2
 * @returns {Record<number, boolean>}
 */
const calculateEligibility = (
  { hasV2Preview, hasV2Publication, eligible },
  isV2,
) => ({
  [0]: isV2,
  [1]: isV2 && (hasV2Preview || hasV2Publication),
  [2]: isV2 && hasV2Publication,
  [3]: isV2 && eligible,
});

/**
 *
 * @param {Object} props
 * @param {number} props.step
 * @param {boolean} props.complete
 * @returns
 */
const IndicatorIcon = ({ step, complete }) => {
  return (
    <div
      className={styles.indicatorIcon}
      data-complete={complete}
      data-step={step}
    >
      <span>{step}</span>
      <Icon type="Upgraded" />
    </div>
  );
};

/**
 * @param {Object} props
 * @param {Record<number, boolean>} props.eligibility
 * @param {number} props.steps
 * @returns
 */
const ProgressIndicator = ({ eligibility, steps }) => {
  return (
    <div
      className={styles.indicatorRow}
      style={{ "--step-count": steps, "--icon-size": "4rem" }}
    >
      {Array.from({ length: steps }).map((_, index, range) => (
        <React.Fragment key={`${index + 1}`}>
          <IndicatorIcon
            step={index + 1}
            complete={index + 1 !== range.length ? eligibility[index] : false}
          />
          {index + 1 !== range.length && (
            <div
              className={styles.indicatorLine}
              data-complete={eligibility[index]}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

const openPreview = () => {
  SiteSettingsActions.closeDialog();
  UIActions.previewOpen();
  UserActions.trackEvent({ event: PREVIEW }, { fb: true });
};

const openPublish = () => {
  SiteSettingsActions.closeDialog();
  SitePublishActions.openDialog();
  UserActions.trackEvent({ event: PUBLISH_OPEN });
};

/**
 * Upgrade tab of the Site Settings modal. Walks the customer through the
 * four-step path onto the v2 Publish Engine: activate the beta toggle,
 * preview, publish, upgrade. The tab is hidden entirely by SiteSettingsModal
 * once the design is locked; the isLocked short-circuit below is
 * belt-and-braces.
 *
 * @returns {React.ReactElement|null}
 */
const UpgradePanel = () => {
  const siteId = useSiteState().get("site_design_id");
  const { useProtorenderer: isV2 } = useRenderer(siteId);
  const { eligibility, eligibilityLoading, eligibilityError } = useRendererLock(
    siteId,
    { enabled: siteId != null },
  );
  const openLockModal = () =>
    UIActions.modalOpen(AppConstants.MODAL_RENDERER_LOCK_IN);

  if (eligibilityError) {
    return (
      <Box m="3" className={styles.errorState}>
        <Text variant="body" tag="p">
          Unable to fetch upgrade data. Please try again in a moment.
        </Text>
      </Box>
    );
  }

  if (eligibilityLoading || !eligibility) {
    return null;
  }

  if (eligibility.isLocked) {
    return null;
  }

  const stepEligibility = calculateEligibility(eligibility, isV2);

  return (
    <Box m="3">
      <Box pb="3">
        <Text className={styles.title} variant="body-strong" tag="p">
          Unlock what&rsquo;s next in Showit
        </Text>
      </Box>

      <Box pb="3">
        <Text variant="body" tag="p">
          We&rsquo;re building new Showit features on an entirely new publishing
          foundation. You can test your site using the Beta Publish Engine,
          verify everything works as expected, publish with confidence, and
          upgrade when you&rsquo;re ready.
        </Text>
      </Box>

      <Box pb="4">
        <Text className={styles.emphasis} variant="body-strong" tag="p">
          Upgraded sites get access to new Showit features as they&rsquo;re
          released.
        </Text>
      </Box>

      <ProgressIndicator steps={4} eligibility={stepEligibility} />

      <div className={styles.stepGrid}>
        <ProtorendererToggle
          label="Use Beta"
          showDescription={false}
          showTooltip={false}
        />
        <Button
          variant="minimal"
          iconPrefix="preview"
          disabled={!isV2}
          onSIClick={openPreview}
          data-state={stepEligibility["1"] ? "done" : "pending"}
        >
          <span className={styles.buttonContent}>PREVIEW</span>
        </Button>
        <Button
          variant="minimal"
          iconPrefix="publish"
          disabled={!isV2}
          onSIClick={openPublish}
          data-state={stepEligibility["2"] ? "done" : "pending"}
        >
          <span className={styles.buttonContent}>PUBLISH</span>
        </Button>
        <Button
          variant="feature"
          iconPrefix="checkFill"
          disabled={!stepEligibility["3"]}
          onSIClick={openLockModal}
          data-state={isV2 ? "ready" : "pending"}
        >
          <span className={styles.buttonContent}>UPGRADE</span>
        </Button>

        <Text className={styles.stepHeader} variant="body-strong" tag="p">
          Activate Beta
        </Text>
        <Text className={styles.stepHeader} variant="body-strong" tag="p">
          Preview &amp; Verify
        </Text>
        <Text className={styles.stepHeader} variant="body-strong" tag="p">
          Publish with Beta
        </Text>
        <Text className={styles.stepHeader} variant="body-strong" tag="p">
          Upgrade*
        </Text>

        <Text variant="body" tag="p">
          Experience the new Publish Engine while keeping the flexibility to
          switch back.
        </Text>
        <Text variant="body" tag="p">
          Preview your site to ensure everything looks and works exactly as
          expected.
        </Text>
        <Text variant="body" tag="p">
          When you&rsquo;re ready, publish your site with the new Engine.
        </Text>
        <Text variant="body" tag="p">
          Gain access to the next generation of Showit features compatible only
          on the new Engine.
        </Text>

        <Text className={styles.footnote} variant="body" tag="p">
          *We&rsquo;ll save a backup of your current site before upgrading.
        </Text>
      </div>
    </Box>
  );
};

export default UpgradePanel;
